import { styled } from '@mui/system';
import { Box, Typography } from '@mui/material';
import { colors, fontFamilies } from '../../../../../constants/theme';
import { figmaLetterSpacing } from '../../../../../ui/utils';
import { Gapper } from '../../../../../ui/utils';

export const Wrapper = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: 20,
  maxWidth: 1100,
  width: '100%',
  [theme.breakpoints.down('md')]: {
    flexDirection: 'column',
    gap: 30,
    padding: '0 20px',
  },
  [theme.breakpoints.down('sm')]: {
    gap: 24,
    padding: '0 16px',
  },
}));

export const StyledGapper = styled(Gapper)(({ theme }) => ({
  flexDirection: 'row',
  alignItems: 'stretch', // both cards share the same height
  [theme.breakpoints.down('md')]: {
    flexDirection: 'column',
  },
}));

export const InfoCard = styled(Box)(({ theme }) => ({
  flex: 1,
  minWidth: 0,
  display: 'flex',
  flexDirection: 'column',
  padding: 10,
  background: colors.whiteText,
  borderRadius: 40,
  boxShadow: `
  -4px -4px 17.8px 0px rgba(205, 214, 223, 0.15),
   4px  4px 15px   0px rgba(205, 214, 223, 0.25)
`,
  [theme.breakpoints.down('md')]: {
    borderRadius: 24,
    padding: 8,
  },
  [theme.breakpoints.down('sm')]: {
    borderRadius: 16,
    padding: 6,
  },
}));

export const InfoCardInner = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: 77,
  padding: 50,
  background: colors.iconBackground,
  borderRadius: 32,
  height: '100%',
  justifyContent: 'flex-start',
  [theme.breakpoints.down('md')]: {
    gap: 40,
    padding: 30,
    borderRadius: 20,
  },
  [theme.breakpoints.down('sm')]: {
    gap: 30,
    padding: 24,
    borderRadius: 16,
  },
}));

export const InfoCardTitle = styled(Typography)(({ theme }) => ({
  ...theme.typography.heading,
  fontSize: 50,
  letterSpacing: figmaLetterSpacing(50, -1),
  lineHeight: '58px',
  [theme.breakpoints.down('md')]: {
    fontSize: 36,
    letterSpacing: figmaLetterSpacing(36, -1),
    lineHeight: '42px',
  },
  [theme.breakpoints.down('sm')]: {
    fontSize: 28,
    letterSpacing: figmaLetterSpacing(28, -1),
    lineHeight: '34px',
  },
}));

export const InfoCardSubtitle = styled(Typography)(({ theme }) => ({
  ...theme.typography.subtext,
  fontSize: 22,
  lineHeight: 'normal',
  letterSpacing: figmaLetterSpacing(22, 3),
  textAlign: 'left',
  color: theme.palette.text.primary,
  fontWeight: 500,
  [theme.breakpoints.down('md')]: {
    fontSize: 20,
    letterSpacing: figmaLetterSpacing(20, 3),
  },
  [theme.breakpoints.down('sm')]: {
    fontSize: 18,
    letterSpacing: figmaLetterSpacing(18, 3),
  },
}));

export const InfoCardText = styled(InfoCardSubtitle)(({ theme }) => ({
  fontWeight: 400,
  whiteSpace: 'pre-line',
  maxWidth: '420px',
  [theme.breakpoints.down('md')]: {
    maxWidth: '100%',
  },
}));

export const Form = styled('form')(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  maxWidth: 540,
  width: '100%',
  gap: 50,
  alignItems: 'flex-start',
  [theme.breakpoints.down('md')]: {
    maxWidth: '100%',
    gap: 40,
  },
  [theme.breakpoints.down('sm')]: {
    gap: 30,
  },
}));

export const FormSection = styled(Box)(({ theme }) => ({
  width: '100%',
  maxWidth: '540px',
  [theme.breakpoints.down('md')]: {
    maxWidth: '100%',
  },
}));

// Right-hand "Ready to Reach Out?" card — mirrors InfoCard's shell so the two
// columns read as a matched pair, but its content is centered around the CTA.
export const CtaCardInner = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  textAlign: 'center',
  gap: 24,
  padding: 50,
  height: '100%',
  background: colors.iconBackground,
  borderRadius: 32,
  [theme.breakpoints.down('md')]: {
    gap: 20,
    padding: 30,
    borderRadius: 20,
  },
  [theme.breakpoints.down('sm')]: {
    gap: 18,
    padding: 24,
    borderRadius: 16,
  },
}));

export const CtaIconWrap = styled(Box)(({ theme }) => ({
  width: 76,
  height: 76,
  borderRadius: '50%',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  background: colors.whiteText,
  color: colors.secondary,
  boxShadow:
    '-4px -4px 17.8px 0px rgba(205, 214, 223, 0.15), 4px 4px 15px 0px rgba(205, 214, 223, 0.25)',
  '& svg': { fontSize: 38 },
  [theme.breakpoints.down('sm')]: {
    width: 60,
    height: 60,
    '& svg': { fontSize: 30 },
  },
}));

export const CtaTitle = styled(InfoCardTitle)(({ theme }) => ({
  fontSize: 40,
  letterSpacing: figmaLetterSpacing(40, -1),
  lineHeight: '48px',
  whiteSpace: 'nowrap', // keep "Ready to Reach Out?" on one line
  [theme.breakpoints.down('md')]: {
    fontSize: 32,
    lineHeight: '40px',
  },
  [theme.breakpoints.down('sm')]: {
    fontSize: 22,
    lineHeight: '30px',
  },
}));

export const CtaText = styled(InfoCardText)({
  textAlign: 'center',
  maxWidth: 360,
});

export const ConsentText = styled(Typography)(({ theme }) => ({
  ...theme.typography.subtext,
  fontFamily: fontFamilies.quicksand,
  fontSize: 16,
  fontWeight: 500,
  color: theme.palette.text.secondary,
  textAlign: 'left',
  marginTop: 10,
}));
